import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { OrderQueueCard } from "@/components/workshop/OrderQueueCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { Countdown } from "@/components/common/Countdown";
import { Money } from "@/components/common/Money";
import { toast } from "sonner";
import { Search, FileText, RefreshCw, Clock } from "lucide-react";

interface QuoteOrder {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  itemCount: number;
  items: { id: string; garmentType: string; qty: number; tagCode?: string }[];
  customer: { publicId: string; name: string; phone?: string };
  address: { line1: string; line2?: string; city: string; pincode: string };
  area: { name: string };
  activeQuote?: {
    id: string;
    versionNo: number;
    status: string;
    total: number;
    expiresAt?: string;
  };
  pickupShipment?: {
    status: string;
    slot: { startAt: string; endAt: string };
  };
  createdAt: string;
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<QuoteOrder[]>([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"needs_quote" | "awaiting_payment">("needs_quote");

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoading(true);
    try {
      const response = await fetch(`/api/workshop/orders?stage=quotes`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }

  const needsQuoteOrders = orders.filter(
    (o) =>
      ["RECEIVED_AT_WORKSHOP", "INSPECTION_IN_PROGRESS"].includes(o.status) &&
      (!o.activeQuote || o.activeQuote.status === "DRAFT")
  );

  const awaitingPaymentOrders = orders.filter(
    (o) => o.status === "AWAITING_PAYMENT"
  );

  const filteredOrders =
    tab === "needs_quote" ? needsQuoteOrders : awaitingPaymentOrders;

  const searchFiltered = search
    ? filteredOrders.filter(
        (o) =>
          o.publicId.toLowerCase().includes(search.toLowerCase()) ||
          o.customer.publicId.toLowerCase().includes(search.toLowerCase()) ||
          o.customer.phone?.includes(search)
      )
    : filteredOrders;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Quotes"
        actions={
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as "needs_quote" | "awaiting_payment")}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList>
            <TabsTrigger value="needs_quote" className="relative">
              Needs Quote
              {needsQuoteOrders.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {needsQuoteOrders.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="awaiting_payment">
              Awaiting Payment
              {awaitingPaymentOrders.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {awaitingPaymentOrders.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>

        <TabsContent value="needs_quote" className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-40 w-full" />
              ))}
            </div>
          ) : searchFiltered.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No orders need quoting"
              description="Orders requiring quotes will appear here after intake."
            />
          ) : (
            <div className="space-y-4">
              {searchFiltered.map((order) => (
                <OrderQueueCard
                  key={order.id}
                  order={order}
                  action={{
                    label: order.activeQuote?.status === "DRAFT" ? "Edit Quote" : "Create Quote",
                    onClick: () => navigate(`/workshop/orders/${order.id}`),
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="awaiting_payment" className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-40 w-full" />
              ))}
            </div>
          ) : searchFiltered.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No orders awaiting payment"
              description="Orders with sent quotes will appear here."
            />
          ) : (
            <div className="space-y-4">
              {searchFiltered.map((order) => (
                <div key={order.id} className="relative">
                  <OrderQueueCard
                    order={order}
                    action={{
                      label: "View Order",
                      onClick: () => navigate(`/workshop/orders/${order.id}`),
                    }}
                  />
                  {order.activeQuote && (
                    <div className="absolute top-4 right-4 flex items-center gap-3">
                      <Badge variant="outline">v{order.activeQuote.versionNo}</Badge>
                      <Money amount={order.activeQuote.total} className="font-semibold" />
                      {order.activeQuote.expiresAt && (
                        <Countdown
                          targetDate={order.activeQuote.expiresAt}
                          label="Expires:"
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
